/**
 * Cookie Consent Banner Component
 * Displays consent options and manages user preferences
 * Compliant with privacy regulations
 */

import React, { useState, useEffect } from 'react';
import { getCookieConsentManager } from '../utils/cookie-consent';

export const CookieConsentBanner = () => {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const consentManager = getCookieConsentManager();

  useEffect(() => {
    // Show banner only if user hasn't given consent
    if (!consentManager.isConsentGiven()) {
      setShow(true);
    }
  }, []);

  const handleAcceptAll = () => {
    consentManager.acceptAll();
    setShow(false);
  };

  const handleAcceptNecessary = () => {
    consentManager.acceptNecessary();
    setShow(false);
  };

  const handleSavePreferences = (preferences) => {
    consentManager.savePreferences(preferences);
    setShow(false);
  };

  if (!show) {
    return null;
  }

  return (
    <>
      {!showDetails ? (
        <CookieConsentNotice
          onAcceptAll={handleAcceptAll}
          onAcceptNecessary={handleAcceptNecessary}
          onShowDetails={() => setShowDetails(true)}
        />
      ) : (
        <CookieConsentDetails
          onSave={handleSavePreferences}
          onBack={() => setShowDetails(false)}
        />
      )}
    </>
  );
};

/**
 * Cookie Consent Notice Component
 */
const CookieConsentNotice = ({
  onAcceptAll,
  onAcceptNecessary,
  onShowDetails,
}) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-[#08080a]/95 backdrop-blur-md border-t border-gray-100 dark:border-[#1d1e26] shadow-[0_-4px_20px_rgba(0,0,0,0.05)] dark:shadow-none z-[60] transition-colors duration-200">
      <div className="max-w-[1400px] mx-auto px-4 py-3 sm:px-6">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xl">🍪</span>
            <p className="text-[13px] text-gray-500 dark:text-gray-400 leading-tight max-w-3xl">
              <span className="font-bold text-gray-900 dark:text-gray-100 mr-1">Cookie Preferences:</span>
              We use cookies to enhance your experience and make the app faster. We respect your privacy and only use essential cookies by default.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onShowDetails}
              className="px-4 py-2 text-xs font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#17181e] rounded-md transition-colors duration-200"
            >
              Customize
            </button>
            <button
              onClick={onAcceptNecessary}
              className="px-4 py-2 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-[#111216] hover:bg-gray-100 dark:hover:bg-[#17181e] border border-gray-200 dark:border-[#1d1e26] rounded-md transition-colors duration-200"
            >
              Necessary Only
            </button>
            <button
              onClick={onAcceptAll}
              className="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 dark:bg-[#169873] dark:hover:bg-[#0e7f5f] dark:text-[#08080a] rounded-md shadow-sm transition-all active:scale-95 duration-200"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Cookie Consent Details Component
 */
const CookieConsentDetails = ({ onSave, onBack }) => {
  const consentManager = getCookieConsentManager();
  const [preferences, setPreferences] = useState(consentManager.getPreferences());

  const handleToggle = (key) => {
    if (key === 'necessary') return; 
    setPreferences({
      ...preferences,
      [key]: !preferences[key],
    });
  };

  const handleSave = () => {
    onSave(preferences);
  };

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-[#08080a] rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col border dark:border-[#1d1e26] transition-colors duration-200">
        <div className="px-5 py-3.5 border-b border-gray-100 dark:border-[#1d1e26] flex items-center justify-between bg-gray-50/50 dark:bg-[#111216]/50 transition-colors duration-200">
          <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            ⚙️ Cookie Settings
          </h2>
          <button
            onClick={onBack}
            className="p-1 hover:bg-gray-200 dark:hover:bg-[#17181e] rounded-full transition-colors text-gray-400 dark:text-gray-500 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4 bg-white dark:bg-[#08080a] transition-colors duration-200">
          <CookieTypeSection
            title="Essential"
            description="Required for the app to function properly (auth, security, speed)."
            type="necessary"
            enabled={true}
            disabled={true}
            onChange={handleToggle}
          />

          <CookieTypeSection
            title="Performance"
            description="Local data caching for instant navigation and offline access."
            type="caching"
            enabled={preferences.caching}
            onChange={handleToggle}
          />

          <CookieTypeSection
            title="Analytics"
            description="Anonymous usage data to help us improve the platform."
            type="analytics"
            enabled={preferences.analytics}
            onChange={handleToggle}
          />

          <CookieTypeSection
            title="Marketing"
            description="Relevant content and notifications based on your activity."
            type="marketing"
            enabled={preferences.marketing}
            onChange={handleToggle}
          />

          <div className="bg-emerald-50/50 dark:bg-emerald-950/20 rounded-lg p-3 text-[11px] text-gray-500 dark:text-gray-400 border border-emerald-100/50 dark:border-emerald-900/30 transition-colors duration-200">
            <p>
              <strong>Privacy:</strong> We never sell your data. View our{' '}
              <a href="/privacy" className="text-emerald-600 dark:text-[#10E58F] font-bold hover:underline">Privacy Policy</a>.
            </p>
          </div>
        </div>

        <div className="px-5 py-3 bg-gray-50/50 dark:bg-[#111216]/50 border-t border-gray-100 dark:border-[#1d1e26] flex gap-2 justify-end transition-colors duration-200">
          <button
            onClick={onBack}
            className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 bg-white dark:bg-[#111216] border border-gray-200 dark:border-[#1d1e26] rounded-md shadow-sm transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 dark:bg-[#169873] dark:hover:bg-[#0e7f5f] dark:text-[#08080a] rounded-md shadow-sm transition-all active:scale-95 duration-200"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Individual Cookie Type Section
 */
const CookieTypeSection = ({
  title,
  description,
  type,
  enabled,
  disabled,
  onChange,
}) => {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-gray-50 dark:border-[#1d1e26]/40 last:border-b-0 transition-colors duration-200">
      <div className="flex-1">
        <h3 className="text-xs font-bold text-gray-900 dark:text-gray-100 leading-none mb-1">{title}</h3>
        <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-tight">{description}</p>
      </div>
      <div className="flex-shrink-0">
        <button
          onClick={() => onChange(type)}
          disabled={disabled}
          className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-200 ${
            enabled ? 'bg-emerald-500 dark:bg-[#169873]' : 'bg-gray-200 dark:bg-[#1d1e26]'
          } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
          role="switch"
          aria-checked={enabled}
        >
          <span
            className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
              enabled ? 'translate-x-4' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
